import {
  BasicInfo,
  Cart,
  CartItem,
  Item,
  ItemOffer,
  ItemOptionsQuantities,
  OptionGroup
} from '@/types'
import { computeItemTotal, sumCartItems } from '@/lib/pricing'
import { normalizePortionGroups } from '@/lib/option-group-portions'


interface BackendCartOption {
  id: string
  optionId: string
  optionGroupId: string
  quantity: number
  specialInstructions: string
  options: BackendCartOption[] | null
}

interface BackendCartItem {
  id: string
  itemOfferId: string
  quantity: number
  specialInstructions: string
  options: BackendCartOption[] | null
}

interface BackendCartResponse {
  cartId: string
  customerId: {
    id: string
    name: string
    documentNumber: string
    phone: { number: string, extension: string }
    email: string
  }
  merchantId: {
    id: string
    name: string
  }
  items: BackendCartItem[]
}


export class CartReconstructionService {
  static reconstructCartFromBackend(
    backendData: BackendCartResponse,
    items: Item[],
    itemOffers: ItemOffer[],
    optionGroups: OptionGroup[],
    basicInfo: BasicInfo | null,
  ): Cart {

    const reconstructedItems: CartItem[] = backendData.items.map(backendItem => {
      const itemOffer = itemOffers.find(io => io.id === backendItem.itemOfferId)
      const item = items.find(i => itemOffer && i.id === itemOffer.itemId)

      const id = backendItem.id

      if (!item || !itemOffer) {
        throw new Error(`Item ou ItemOffer não encontrado para: ${backendItem.id}`)
      }

      const optionQuantities: Record<string, ItemOptionsQuantities> = {}
      const itemOptions: Record<string, Item> = {}
      const usedOptionGroups: OptionGroup[] = []
      const optionSpecialInstructions: Record<string, Record<string, string>> = {}

      if (backendItem.options) {
        backendItem.options.forEach(option => {
          const optionGroup = optionGroups.find(og => og.id === option.optionGroupId)

          if (!optionGroup) {
            console.warn(`OptionGroup não encontrado para optionGroupId: ${option.optionGroupId}`)
            return
          }

          const optionDef = optionGroup.options.find(opt => opt.id === option.optionId)

          if (!optionDef) {
            console.warn(`Opção não encontrada para optionId: ${option.optionId} no optionGroup: ${option.optionGroupId}`)
            return
          }

          const optionItem = items.find(i => i.id === optionDef.itemId)
          if (!optionItem) {
            console.warn(`Item não encontrado para option.itemId: ${optionDef.itemId}`)
            return
          }

          if (!optionQuantities[optionGroup.id]) {
            optionQuantities[optionGroup.id] = {}
            usedOptionGroups.push(optionGroup)
          }

          optionQuantities[optionGroup.id][optionDef.id] = option.quantity
          itemOptions[optionDef.id] = optionItem

          if (option.specialInstructions && option.specialInstructions.trim().length > 0) {
            if (!optionSpecialInstructions[optionGroup.id]) {
              optionSpecialInstructions[optionGroup.id] = {}
            }
            optionSpecialInstructions[optionGroup.id][optionDef.id] = option.specialInstructions
          }
        })
      }

      Object.assign(optionQuantities, normalizePortionGroups(usedOptionGroups, optionQuantities))

      const unitPrice = computeItemTotal(itemOffer, usedOptionGroups, optionQuantities)
      const itemTotalPrice = unitPrice * backendItem.quantity

      const cartItem: CartItem = {
        id,
        item,
        itemOffer,
        itemOptions,
        optionGroups: usedOptionGroups,
        optionQuantities,
        quantity: backendItem.quantity,
        itemTotalPrice,
        unitPrice,
        specialInstructions: backendItem.specialInstructions || '',
        optionSpecialInstructions: Object.keys(optionSpecialInstructions).length > 0
          ? optionSpecialInstructions
          : undefined,
      }

      return cartItem
    })

    const totalPrice = sumCartItems(reconstructedItems)

    const reconstructedCart: Cart = {
      items: reconstructedItems,
      totalPrice,
      estimateTakeoutTime: basicInfo?.averagePreparationTime,
    }
    return reconstructedCart
  }


  static validateReconstructionData(
    items: Item[],
    itemOffers: ItemOffer[],
    optionGroups: OptionGroup[]
  ): boolean {
    return items.length > 0 && itemOffers.length > 0 && optionGroups.length > 0
  }
}